import type React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Settings,
  AlertTriangle,
  Info,
  FileText,
  Clock,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { cn, formatTime } from '@/lib/utils';
import type { DisputeType, WorkflowStage } from '@/types/dispute-types';
import { useTemplatesList } from '@/hooks/useTemplatesData';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { useActiveDisputeCount } from '@/hooks/useDisputeTypesData';
import { useTableSort } from '@/hooks/useTableSort';
import type { DisputeAuditLogEntry } from '@/lib/mock-data/dispute-audit-log-mock-data';
import { useDisputeTypeAuditLogs } from '@/hooks/useDisputeTypeAuditLogs';

// Types
export interface WorkflowLevel {
  id: string;
  levelNumber: number;
  description: string;
  followUpDays: number | '';
  escalationDays: number | '';
  assignedUser: string;
  escalationUser: string;
  documentTemplate: string;
  sendCorrespondence: boolean;
}

export interface WorkflowData {
  workflowName: string;
  description: string;
  levels: WorkflowLevel[];
  resolvedStage?: WorkflowStage;
}

interface WorkflowEditorProps {
  disputeType?: DisputeType | null;
  onSave?: (workflow: WorkflowData) => void;
  isSaving?: boolean;
}

export function WorkflowEditor({ disputeType, onSave, isSaving = false }: WorkflowEditorProps) {
  const navigate = useNavigate();
  const [workflowName, setWorkflowName] = useState('');
  const [description, setDescription] = useState('');
  const [levels, setLevels] = useState<WorkflowLevel[]>([]);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('settings');
  const [resolvedStage, setResolvedStage] = useState<WorkflowStage | null>(null);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [pendingWorkflowData, setPendingWorkflowData] = useState<WorkflowData | null>(null);

  // Fetch active dispute count dynamically from API
  const { data: activeDisputeCount = 0 } = useActiveDisputeCount(disputeType?.id ?? null);

  // Fetch email templates
  const { data: templatesData } = useTemplatesList();
  const emailTemplates = templatesData?.filter((t) => t.type === 'email') || [];

  // Fetch users from API
  const { data: users = [] } = useCompanyUsersList();
  const activeUsers = users.map((u) => ({ id: u.id, name: `${u.firstName} ${u.lastName}` }));

  // Fetch audit log data (uses mock data in Demo mode, live GraphQL API in Live mode)
  const { auditLogs: auditLog, isLoading: auditLogLoading } = useDisputeTypeAuditLogs(disputeType?.id?.toString());

  // Helper to resolve template name from ID
  const getTemplateName = (templateId: string): string => {
    if (templateId === 'None') return 'None';
    return emailTemplates.find((t) => t.id === templateId)?.name || templateId;
  };

  // Helper to resolve user name from ID
  const getUserName = (userId: string): string => {
    if (userId === 'Unassigned') return 'Unassigned';
    return activeUsers.find((u) => u.id === userId)?.name || userId;
  };

  // Convert workflow stages to levels format
  const convertStagesToLevels = (disputeType: DisputeType): WorkflowLevel[] => {
    if (!disputeType.workflow?.stages || disputeType.workflow.stages.length === 0) {
      return [];
    }

    return disputeType.workflow.stages.map((stage) => {
      const slaHours = stage.properties?.slaHours ?? 24;
      const slaWarningHours = stage.properties?.slaWarningHours ?? 20;

      // Convert hours to days (rounded)
      const followUpDays = Math.ceil(slaWarningHours / 24);
      const escalationDays = Math.ceil(slaHours / 24);

      // Read stored values directly from stage properties (set during creation)
      const storedAssignedUser = stage.properties?.allowedAssigneeRoles?.[0];
      const storedEscalationUser = stage.properties?.escalationUser || stage.properties?.escalationRoles?.[0];
      const storedDocumentTemplate = stage.properties?.documentTemplate;
      const sendCorrespondence = stage.properties?.sendCorrespondence ?? true;

      // Use stored values if available, otherwise fall back to mapping helpers
      const assignedUser = storedAssignedUser || 'Unassigned';
      const escalationUser = storedEscalationUser || 'Unassigned';
      const documentTemplate = storedDocumentTemplate || 'None';

      return {
        id: stage.id,
        levelNumber: stage.order,
        description: stage.description || '',
        followUpDays,
        escalationDays,
        assignedUser,
        escalationUser,
        documentTemplate,
        sendCorrespondence,
      };
    });
  };

  // Generate mock workflow data based on dispute type (fallback for old data)
  const generateWorkflowData = (disputeTypeName: string) => {
    const workflowConfigs: Record<
      string,
      {
        workflowName: string;
        description: string;
        levels: WorkflowLevel[];
      }
    > = {
      'Billing Error': {
        workflowName: 'Billing Error Resolution Workflow',
        description: 'Standard workflow for handling billing errors and pricing discrepancies',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'John Smith',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 3,
            escalationDays: 8,
            assignedUser: 'John Smith',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Dispute Investigation Letter',
            sendCorrespondence: true,
          },
          {
            id: 'level-3',
            levelNumber: 3,
            description: 'Escalation level - Dispute is escalated to a higher authority or specialist',
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Michael Chen',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Final Resolution Notice',
            sendCorrespondence: true,
          },
        ],
      },
      'Service Quality': {
        workflowName: 'Service Quality Dispute Workflow',
        description: 'Workflow for handling service delivery quality and performance issues',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Emma Williams',
            escalationUser: 'David Martinez',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 5,
            escalationDays: 12,
            assignedUser: 'David Martinez',
            escalationUser: 'John Smith',
            documentTemplate: 'Dispute Investigation Letter',
            sendCorrespondence: true,
          },
          {
            id: 'level-3',
            levelNumber: 3,
            description: 'Escalation level - Dispute is escalated to a higher authority or specialist',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'John Smith',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Payment Plan Proposal',
            sendCorrespondence: true,
          },
          {
            id: 'level-4',
            levelNumber: 4,
            description: 'Senior escalation level - Further escalation to senior management or specialized teams',
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Final Resolution Notice',
            sendCorrespondence: true,
          },
        ],
      },
      'Payment Dispute': {
        workflowName: 'Payment Dispute Resolution Workflow',
        description: 'Fast-track workflow for payment processing and allocation errors',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 1,
            escalationDays: 3,
            assignedUser: 'Michael Chen',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'John Smith',
            documentTemplate: 'Final Resolution Notice',
            sendCorrespondence: true,
          },
        ],
      },
      'Contract Terms': {
        workflowName: 'Contract Terms Dispute Workflow',
        description: 'Comprehensive workflow for contract interpretation and terms disagreements',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'David Martinez',
            escalationUser: 'John Smith',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 5,
            escalationDays: 14,
            assignedUser: 'John Smith',
            escalationUser: 'Emma Williams',
            documentTemplate: 'Dispute Investigation Letter',
            sendCorrespondence: true,
          },
          {
            id: 'level-3',
            levelNumber: 3,
            description: 'Escalation level - Dispute is escalated to a higher authority or specialist',
            followUpDays: 4,
            escalationDays: 10,
            assignedUser: 'Emma Williams',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Payment Plan Proposal',
            sendCorrespondence: true,
          },
          {
            id: 'level-4',
            levelNumber: 4,
            description: 'Senior escalation level - Further escalation to senior management or specialized teams',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Escalation to Management',
            sendCorrespondence: true,
          },
          {
            id: 'level-5',
            levelNumber: 5,
            description: 'Final escalation level - Highest level of escalation before legal or external resolution',
            followUpDays: 2,
            escalationDays: 5,
            assignedUser: 'Michael Chen',
            escalationUser: 'David Martinez',
            documentTemplate: 'Final Resolution Notice',
            sendCorrespondence: true,
          },
        ],
      },
      'Duplicate Charge': {
        workflowName: 'Duplicate Charge Resolution Workflow',
        description: 'Quick resolution workflow for duplicate invoices and charges',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 1,
            escalationDays: 2,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 1,
            escalationDays: 3,
            assignedUser: 'Michael Chen',
            escalationUser: 'John Smith',
            documentTemplate: 'Final Resolution Notice',
            sendCorrespondence: true,
          },
        ],
      },
      'Product Defect': {
        workflowName: 'Product Defect Dispute Workflow',
        description: 'Workflow for handling defective products and goods not as described',
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Emma Williams',
            escalationUser: 'David Martinez',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 5,
            escalationDays: 10,
            assignedUser: 'David Martinez',
            escalationUser: 'John Smith',
            documentTemplate: 'Dispute Investigation Letter',
            sendCorrespondence: true,
          },
          {
            id: 'level-3',
            levelNumber: 3,
            description: 'Escalation level - Dispute is escalated to a higher authority or specialist',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'John Smith',
            escalationUser: 'Sarah Johnson',
            documentTemplate: 'Final Resolution Notice',
            sendCorrespondence: true,
          },
        ],
      },
    };

    return (
      workflowConfigs[disputeTypeName] || {
        workflowName: disputeTypeName,
        description: `Standard workflow for handling ${disputeTypeName.toLowerCase()}`,
        levels: [
          {
            id: 'level-1',
            levelNumber: 1,
            description: 'Initial contact level - First attempt to resolve the dispute with assigned user',
            followUpDays: 3,
            escalationDays: 7,
            assignedUser: 'Sarah Johnson',
            escalationUser: 'John Smith',
            documentTemplate: 'Initial Dispute Acknowledgment',
            sendCorrespondence: true,
          },
          {
            id: 'level-2',
            levelNumber: 2,
            description: 'Follow-up level - Second attempt if the dispute remains unresolved after Level 1',
            followUpDays: 5,
            escalationDays: 10,
            assignedUser: 'John Smith',
            escalationUser: 'Michael Chen',
            documentTemplate: 'Dispute Investigation Letter',
            sendCorrespondence: true,
          },
        ],
      }
    );
  };

  // Load initial data or set defaults
  useEffect(() => {
    if (disputeType) {
      // Try to use actual workflow stages first
      const actualLevels = convertStagesToLevels(disputeType);

      if (actualLevels.length > 0) {
        // Use actual workflow data
        setWorkflowName(disputeType.name);
        setDescription(disputeType.description);

        // Check if the last stage is the system-generated "Resolved" stage and filter it out
        const stages = disputeType.workflow?.stages || [];
        const lastStage = stages[stages.length - 1];
        const isResolvedStage =
          lastStage &&
          (lastStage.name === 'Resolved' ||
            lastStage.description?.toLowerCase().includes('resolved') ||
            lastStage.description?.toLowerCase().includes('closed')) &&
          (!lastStage.properties?.slaHours || lastStage.properties.slaHours === 0);

        if (isResolvedStage) {
          setResolvedStage(lastStage);
          const editableLevels = actualLevels.filter((l) => l.id !== lastStage.id);
          setLevels(editableLevels);
        } else {
          setLevels(actualLevels);
        }
      } else {
        // Fallback to generated mock data
        const workflowData = generateWorkflowData(disputeType.name);
        setWorkflowName(workflowData.workflowName);
        setDescription(workflowData.description);
        setLevels(workflowData.levels);
      }
      setErrors({});
    }
  }, [disputeType]);

  // Keep `errors` state in sync with form inputs so inline validation messages
  // appear immediately while editing, instead of only after the user clicks the disabled Save button.
  useEffect(() => {
    setErrors(getValidationErrorsMap());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowName, levels]);

  // Calculate timeline data
  const timelineData = useMemo(() => {
    const levelTimelines = levels.map((level) => {
      return {
        levelNumber: level.levelNumber,
        followUpDays: level.followUpDays,
        escalationDays: level.escalationDays,
        assignedUser: level.assignedUser,
        escalationUser: level.escalationUser,
        documentTemplate: level.documentTemplate,
        sendCorrespondence: level.sendCorrespondence,
      };
    });

    const totalDuration = levels.reduce(
      (sum, level) => sum + (Number(level.followUpDays) || 0) + (Number(level.escalationDays) || 0),
      0,
    );

    return { levelTimelines, totalDuration };
  }, [levels]);

  // Check if workflow is fully configured (no unassigned users)
  const isFullyConfigured = (): boolean => {
    return levels.every((level) => level.assignedUser && level.assignedUser !== 'Unassigned');
  };

  // Count incomplete levels
  const getIncompleteCount = (): number => {
    return levels.filter((level) => level.assignedUser === 'Unassigned').length;
  };

  // Validation - pure helper (single source of truth for both inline errors and the form-valid check)
  const getValidationErrorsMap = (): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    if (!workflowName.trim()) {
      newErrors.workflowName = 'Workflow name is required';
    }

    if (levels.length === 0) {
      newErrors.levels = 'At least one workflow level is required';
    }

    levels.forEach((level, index) => {
      if (!level.description?.trim()) {
        newErrors[`level-${index}-description`] = 'Description is required';
      }

      if (Number(level.followUpDays) <= 0) {
        newErrors[`level-${index}-followUp`] = 'Follow-up days must be greater than 0';
      }

      if (Number(level.escalationDays) === 0 && level.escalationUser && level.escalationUser !== 'Unassigned') {
        newErrors[`level-${index}-escalationUser`] = 'Cannot set Escalation User when Escalation Days is zero';
      }

      if (!level.assignedUser || level.assignedUser === 'Unassigned') {
        newErrors[`level-${index}-user`] = 'Please assign a user';
      }
    });

    return newErrors;
  };

  const validateForm = (): boolean => {
    const newErrors = getValidationErrorsMap();
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    return Object.keys(getValidationErrorsMap()).length === 0;
  };

  // Get validation error summary for tooltip
  const getValidationErrors = (): string[] => {
    const errorMessages: string[] = [];

    if (workflowName.trim() === '') {
      errorMessages.push('Workflow name is required');
    }

    if (levels.length === 0) {
      errorMessages.push('At least one workflow level is required');
    }

    levels.forEach((level, index) => {
      if (Number(level.followUpDays) <= 0) {
        errorMessages.push(`Level ${index + 1}: Follow-up days must be greater than 0`);
      }

      if (Number(level.escalationDays) === 0 && level.escalationUser && level.escalationUser !== 'Unassigned') {
        errorMessages.push(`Level ${index + 1}: Cannot set Escalation User when Escalation Days is zero`);
      }

      if (!level.assignedUser || level.assignedUser === 'Unassigned') {
        errorMessages.push(`Level ${index + 1}: Please assign a user`);
      }
    });

    return errorMessages;
  };

  // Level management
  const addLevel = () => {
    if (levels.length >= 5) {
      toast.error('Maximum 5 levels reached');
      return;
    }

    const newLevel: WorkflowLevel = {
      id: `level-${Date.now()}`,
      levelNumber: levels.length + 1,
      description: '',
      followUpDays: 1,
      escalationDays: 0,
      assignedUser: 'Unassigned',
      escalationUser: 'Unassigned',
      documentTemplate: 'None',
      sendCorrespondence: true,
    };

    setLevels([...levels, newLevel]);
  };

  const removeLevel = (index: number) => {
    const updatedLevels = levels.filter((_, i) => i !== index);
    // Renumber levels
    const renumberedLevels = updatedLevels.map((level, i) => ({
      ...level,
      levelNumber: i + 1,
    }));
    setLevels(renumberedLevels);
  };

  const updateLevel = (index: number, field: keyof WorkflowLevel, value: string | number | boolean) => {
    const updatedLevels = [...levels];
    updatedLevels[index] = { ...updatedLevels[index], [field]: value };
    setLevels(updatedLevels);
  };

  const moveLevel = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= levels.length) return;

    const updatedLevels = [...levels];
    const [movedLevel] = updatedLevels.splice(fromIndex, 1);
    updatedLevels.splice(toIndex, 0, movedLevel);

    // Renumber levels
    const renumberedLevels = updatedLevels.map((level, i) => ({
      ...level,
      levelNumber: i + 1,
    }));

    setLevels(renumberedLevels);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    moveLevel(draggedIndex, index);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Save handler
  const handleSave = () => {
    if (!validateForm()) {
      toast.error('Please fix all validation errors before saving');
      return;
    }

    const normalizedLevels = levels.map((level) => ({
      ...level,
      followUpDays: Number(level.followUpDays) || 0,
      escalationDays: Number(level.escalationDays) || 0,
    }));

    const workflowData: WorkflowData = {
      workflowName,
      description,
      levels: normalizedLevels,
      resolvedStage: resolvedStage || undefined,
    };

    // If the dispute type has active disputes, show confirmation modal
    if (activeDisputeCount > 0) {
      setPendingWorkflowData(workflowData);
      setShowSaveConfirmation(true);
      return;
    }

    // No active disputes — save directly
    if (onSave) {
      onSave(workflowData);
    }
    toast.success('Workflow updated successfully');
    navigate('/settings/dispute-types');
  };

  const handleConfirmSave = () => {
    if (pendingWorkflowData && onSave) {
      onSave(pendingWorkflowData);
    }
    setShowSaveConfirmation(false);
    setPendingWorkflowData(null);
    toast.success('Workflow updated successfully');
    navigate('/settings/dispute-types');
  };

  const handleCancelSave = () => {
    setShowSaveConfirmation(false);
    setPendingWorkflowData(null);
  };

  const handleCancel = () => {
    navigate('/settings/dispute-types');
  };

  // Audit log sorting - transform data to make user sortable
  const auditLogData = auditLog.map((item) => ({
    ...item,
    userName: item.user.name, // Add flat property for sorting
  }));
  const {
    sortedData: sortedAuditLog,
    requestSort,
    getSortDirection,
  } = useTableSort(auditLogData, { initialSortKey: 'date', initialSortDirection: 'desc' });

  // Helper functions for Audit Log tab
  const getEventColor = (event: string) => {
    switch (event) {
      case 'deactivated':
        return 'bg-red-500';
      case 'activated':
      case 'created':
        return 'bg-green-500';
      case 'workflow_changed':
        return 'bg-blue-500';
      case 'configuration_changed':
      case 'updated':
        return 'bg-orange-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatEventName = (event: string) => {
    return event
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="border-b bg-background pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">
              Edit Dispute Type{disputeType ? `: ${disputeType.name}` : ''}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Button
                      onClick={handleSave}
                      disabled={!isFormValid() || isSaving}
                      className="bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50"
                    >
                      {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Update Workflow
                    </Button>
                  </div>
                </TooltipTrigger>
                {!isFormValid() && (
                  <TooltipContent side="bottom" className="max-w-md">
                    <div className="space-y-1">
                      <p className="font-semibold text-sm">Cannot update workflow:</p>
                      <ul className="text-xs space-y-0.5 list-disc list-inside">
                        {getValidationErrors()
                          .slice(0, 5)
                          .map((error) => (
                            <li key={error}>{error}</li>
                          ))}
                        {getValidationErrors().length > 5 && (
                          <li className="text-muted-foreground">
                            ...and {getValidationErrors().length - 5} more issue(s)
                          </li>
                        )}
                      </ul>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Content - Tabbed Layout */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="border-b bg-background">
          <TabsList className="h-12">
            <TabsTrigger value="settings" className="gap-2">
              <FileText className="h-4 w-4" />
              Settings
            </TabsTrigger>
            {disputeType && (
              <TabsTrigger value="audit-log" className="gap-2">
                <Clock className="h-4 w-4" />
                Audit Log
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-0">
          <div className="container max-w-7xl mx-auto p-6 space-y-6">
            {/* Basic Information Section */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  {/* Left Column - Form Fields */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="workflowName">
                        Workflow Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="workflowName"
                        value={workflowName}
                        onChange={(e) => setWorkflowName(e.target.value)}
                        placeholder="Enter workflow name"
                        className={errors.workflowName ? 'border-red-500' : ''}
                      />
                      {errors.workflowName && <p className="text-sm text-red-500">{errors.workflowName}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter workflow description"
                        maxLength={1000}
                        rows={3}
                      />
                      <p className="text-sm text-muted-foreground">
                        Provide a clear description to help team members understand when to use this workflow.
                      </p>
                    </div>
                  </div>

                  {/* Right Column - Configuration Tips */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4">
                    <div className="flex gap-3">
                      <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                          Workflow Configuration Tips
                        </p>
                        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
                          <li>If Escalation Days set to zero, Dispute will not be escalated</li>
                          <li>All levels must have an assigned user</li>
                          <li>Drag and drop levels to reorder them</li>
                          <li>You can configure up to 5 escalation levels</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Workflow Levels Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Workflow Levels</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Define escalation levels with timelines and assignments
                    </p>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={addLevel}
                          disabled={levels.length >= 5}
                          size="sm"
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Level
                        </Button>
                      </TooltipTrigger>
                      {levels.length >= 5 && (
                        <TooltipContent>
                          <p>Maximum 5 levels reached</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {errors.levels && (
                  <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{errors.levels}</p>
                  </div>
                )}

                {/* Configuration Warning Banner */}
                {levels.length > 0 && !isFullyConfigured() && (
                  <Alert variant="warning" className="mb-4">
                    <AlertDescription>
                      <span className="font-semibold">Setup Incomplete:</span> {getIncompleteCount()} of {levels.length}{' '}
                      level(s) need configuration. Assign users to each workflow level for optimal automated dispute
                      handling.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Workflow Level Cards */}
                <div className="space-y-3">
                  {levels.length === 0 ? (
                    <Card className="border-dashed">
                      <CardContent className="flex flex-col items-center justify-center py-8">
                        <p className="text-muted-foreground mb-4">No workflow levels defined</p>
                        <Button onClick={addLevel} variant="outline" size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Level
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    levels.map((level, index) => (
                      <Card
                        key={level.id}
                        className="border hover:border-teal-300 dark:hover:border-teal-700 transition-colors"
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                      >
                        <CardContent className="py-4">
                          <div className="flex items-start gap-3">
                            {/* Drag Handle and Level Badge */}
                            <div className="flex items-center gap-2 flex-shrink-0 pt-2">
                              <div className="cursor-grab active:cursor-grabbing">
                                <GripVertical className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex items-center gap-1">
                                <Badge className="bg-teal-600 text-white whitespace-nowrap">
                                  Level {level.levelNumber}
                                </Badge>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-help" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-xs">
                                      <p className="font-semibold mb-1">Level {level.levelNumber}</p>
                                      <p className="text-xs">
                                        {level.levelNumber === 1 &&
                                          'Initial contact level - First attempt to resolve the dispute with the assigned user.'}
                                        {level.levelNumber === 2 &&
                                          'Follow-up level - Second attempt if the dispute remains unresolved after Level 1.'}
                                        {level.levelNumber === 3 &&
                                          'Escalation level - Dispute is escalated to a higher authority or specialist.'}
                                        {level.levelNumber === 4 &&
                                          'Senior escalation level - Further escalation to senior management or specialized teams.'}
                                        {level.levelNumber === 5 &&
                                          'Final escalation level - Highest level of escalation before legal or external resolution.'}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </div>

                            {/* Level Content */}
                            <div className="flex-1 space-y-3">
                              {/* Description Field - Full Width */}
                              <div className="space-y-1.5">
                                <Label htmlFor={`description-${index}`} className="text-xs">
                                  Description <span className="text-red-500">*</span>
                                </Label>
                                <Textarea
                                  id={`description-${index}`}
                                  value={level.description || ''}
                                  onChange={(e) => updateLevel(index, 'description', e.target.value)}
                                  placeholder="Enter level description (e.g., Initial contact level - First attempt to resolve the dispute with assigned user)"
                                  maxLength={1000}
                                  rows={2}
                                  className={errors[`level-${index}-description`] ? 'border-red-500' : ''}
                                />
                                {errors[`level-${index}-description`] && (
                                  <p className="text-xs text-red-500">{errors[`level-${index}-description`]}</p>
                                )}
                              </div>

                              {/* All Other Fields in a Single Row */}
                              <div className="grid grid-cols-5 gap-3">
                                {/* Follow Up Days */}
                                <div className="space-y-1.5">
                                  <Label htmlFor={`followUpDays-${index}`} className="text-xs">
                                    Follow Up Days <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                                    id={`followUpDays-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={level.followUpDays}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === '') {
                                        updateLevel(index, 'followUpDays', '');
                                      } else if (/^\d+$/.test(raw)) {
                                        updateLevel(index, 'followUpDays', parseInt(raw));
                                      }
                                    }}
                                    placeholder="Days"
                                    className={errors[`level-${index}-followUp`] ? 'border-red-500 h-9' : 'h-9'}
                                  />
                                  {errors[`level-${index}-followUp`] && (
                                    <p className="text-xs text-red-500">{errors[`level-${index}-followUp`]}</p>
                                  )}
                                </div>

                                {/* Escalation Days */}
                                <div className="space-y-1.5">
                                  <Label htmlFor={`escalationDays-${index}`} className="text-xs">
                                    Escalation Days
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="inline h-3 w-3 ml-1 text-muted-foreground cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          <p className="text-xs">
                                            If Escalation Days set to zero, Dispute will not be escalated
                                          </p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </Label>
                                  <Input
                                    id={`escalationDays-${index}`}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={level.escalationDays}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === '') {
                                        updateLevel(index, 'escalationDays', '');
                                      } else if (/^\d+$/.test(raw)) {
                                        updateLevel(index, 'escalationDays', parseInt(raw));
                                      }
                                    }}
                                    placeholder="Days"
                                    className={errors[`level-${index}-escalation`] ? 'border-red-500 h-9' : 'h-9'}
                                  />
                                  {errors[`level-${index}-escalation`] && (
                                    <p className="text-xs text-red-500">{errors[`level-${index}-escalation`]}</p>
                                  )}
                                </div>

                                {/* Assigned User */}
                                <div className="space-y-1.5">
                                  <Label htmlFor={`assignedUser-${index}`} className="text-xs">
                                    Assigned User <span className="text-red-500">*</span>
                                    {disputeType?.isSystemType && level.assignedUser === 'Unassigned' && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <AlertTriangle className="inline h-3 w-3 ml-1 text-yellow-600" />
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p className="text-xs">Assign a user for automated routing</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </Label>
                                  <Select
                                    value={level.assignedUser}
                                    onValueChange={(value) => updateLevel(index, 'assignedUser', value)}
                                  >
                                    <SelectTrigger
                                      className={errors[`level-${index}-user`] ? 'border-red-500 h-9' : 'h-9'}
                                    >
                                      <SelectValue placeholder="Select user">
                                        {level.assignedUser === 'Unassigned' ? (
                                          <span className="text-muted-foreground">Unassigned</span>
                                        ) : (
                                          activeUsers.find((u) => u.id === level.assignedUser)?.name ||
                                          level.assignedUser
                                        )}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Unassigned">Unassigned</SelectItem>
                                      {activeUsers.map((user) => (
                                        <SelectItem key={user.id} value={user.id}>
                                          {user.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {errors[`level-${index}-user`] && (
                                    <p className="text-xs text-red-500">{errors[`level-${index}-user`]}</p>
                                  )}
                                </div>

                                {/* Escalation User */}
                                <div className="space-y-1.5">
                                  <Label htmlFor={`escalationUser-${index}`} className="text-xs">
                                    Escalation User
                                  </Label>
                                  <Select
                                    value={level.escalationUser}
                                    onValueChange={(value) => updateLevel(index, 'escalationUser', value)}
                                  >
                                    <SelectTrigger
                                      className={errors[`level-${index}-escalationUser`] ? 'border-red-500 h-9' : 'h-9'}
                                    >
                                      <SelectValue placeholder="Select user">
                                        {level.escalationUser === 'Unassigned' ? (
                                          <span className="text-muted-foreground">Unassigned</span>
                                        ) : (
                                          activeUsers.find((u) => u.id === level.escalationUser)?.name ||
                                          level.escalationUser
                                        )}
                                      </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Unassigned">Unassigned</SelectItem>
                                      {activeUsers.map((user) => (
                                        <SelectItem key={user.id} value={user.id}>
                                          {user.name}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {errors[`level-${index}-escalationUser`] && (
                                    <p className="text-xs text-red-500">{errors[`level-${index}-escalationUser`]}</p>
                                  )}
                                </div>

                                {/* Feed Notifications */}
                                <div className="space-y-1.5">
                                  <Label className="text-xs">Feed Notifications</Label>
                                  <div className="flex items-center space-x-2 h-9">
                                    <Switch
                                      checked={level.sendCorrespondence}
                                      onCheckedChange={(checked) => updateLevel(index, 'sendCorrespondence', checked)}
                                    />
                                    <span className="text-xs text-muted-foreground">
                                      {level.sendCorrespondence ? 'Enabled' : 'Disabled'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Delete Button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLevel(index)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20 flex-shrink-0 h-9 w-9 p-0 mt-6"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Workflow Timeline Section */}
            {levels.length > 0 && (
              <Card className="bg-gray-50 dark:bg-gray-900/50">
                <CardHeader>
                  <Collapsible open={isTimelineExpanded} onOpenChange={setIsTimelineExpanded}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full hover:opacity-80">
                      <CardTitle className="text-base">Workflow Timeline Preview</CardTitle>
                      {isTimelineExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <p className="text-sm text-muted-foreground mt-1">
                        Visual representation of the complete workflow timeline
                      </p>
                    </CollapsibleContent>
                  </Collapsible>
                </CardHeader>
                <Collapsible open={isTimelineExpanded} onOpenChange={setIsTimelineExpanded}>
                  <CollapsibleContent>
                    <CardContent className="space-y-3">
                      {timelineData.levelTimelines.map((timeline, index) => (
                        <div key={index} className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-teal-600 text-white">Level {timeline.levelNumber}</Badge>
                            <span className="text-sm font-medium">
                              Follow Up Days {timeline.followUpDays} • Escalation Days {timeline.escalationDays}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Assigned to: {getUserName(timeline.assignedUser)} • Escalates to:{' '}
                            {getUserName(timeline.escalationUser)} • Feed Notifications:{' '}
                            {timeline.sendCorrespondence ? 'Enabled' : 'Disabled'}
                          </p>
                        </div>
                      ))}

                      <div className="pt-2 border-t">
                        <p className="text-sm font-semibold">
                          Total Duration:{' '}
                          <span className="text-teal-600 dark:text-teal-400">
                            Up to {timelineData.totalDuration} days
                          </span>
                        </p>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Audit Log Tab */}
        {disputeType && (
          <TabsContent value="audit-log" className="mt-0">
            <div className="container max-w-7xl mx-auto p-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-blue-600" />
                    Audit Log
                  </CardTitle>
                  <CardDescription>History of changes and modifications to this dispute type workflow</CardDescription>
                </CardHeader>
                <CardContent>
                  {auditLogLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600" />
                      <span className="ml-2 text-sm text-muted-foreground">Loading audit log...</span>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <SortableTableHead
                            sortKey="date"
                            currentSortDirection={getSortDirection('date')}
                            onSort={() => requestSort('date')}
                          >
                            Date & Time
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="event"
                            currentSortDirection={getSortDirection('event')}
                            onSort={() => requestSort('event')}
                          >
                            Event Type
                          </SortableTableHead>
                          <SortableTableHead
                            sortKey="userName"
                            currentSortDirection={getSortDirection('userName')}
                            onSort={() => requestSort('userName')}
                          >
                            User
                          </SortableTableHead>
                          <TableHead>Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedAuditLog.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                              No audit log entries found
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedAuditLog.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell className="whitespace-nowrap">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900">
                                    {format(item.date, 'dd MMM yyyy')}
                                  </span>
                                  <span className="text-xs text-gray-500">{formatTime(item.date)}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div
                                    className={cn('w-2 h-2 rounded-full flex-shrink-0', getEventColor(item.event))}
                                  />
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatEventName(item.event)}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="whitespace-nowrap">
                                <span className="text-sm text-gray-900">{item.user.name}</span>
                              </TableCell>
                              <TableCell>
                                {item.details ? (
                                  <span className="text-sm text-gray-600 break-words">{item.details}</span>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Confirmation modal for saving with active disputes */}
      <AlertDialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Changes to Dispute Type?</AlertDialogTitle>
            <AlertDialogDescription>
              This dispute type has {activeDisputeCount} active dispute(s). Changes you make will apply to new disputes
              only. Existing disputes will continue to follow the workflow configuration that was active when they were
              raised.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelSave}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmSave}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
